import {
  createContext,
  ProviderProps,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import * as Sentry from "@sentry/react";

interface IContext {
  user?: User;
}

export const UserContext = createContext<IContext>({});

const MIN_ATTEMPT_INTERVAL_MS = 2000;

export const UserProvider = (
  props: JSX.IntrinsicAttributes & Omit<ProviderProps<IContext>, "value">,
) => {
  const [user, setUser] = useState<User | null>(null);
  const auth = getAuth();
  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, [auth]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.uid,
        email: user.email ?? undefined,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user !== null) return;

    let cancelled = false;
    const startedAt = Date.now();
    (async () => {
      for (let attempt = 1; !cancelled; attempt++) {
        const attemptStart = Date.now();
        try {
          await signInAnonymously(auth);
          return;
        } catch (error) {
          if (cancelled) return;
          const isNetworkError =
            error instanceof FirebaseError &&
            error.code === "auth/network-request-failed";
          // Non-transient failures (e.g. auth/operation-not-allowed) — retrying
          // won't help. Report as a real Sentry issue and stop.
          if (!isNetworkError) {
            Sentry.captureException(error);
            return;
          }
          const attemptDuration = Date.now() - attemptStart;
          // Every retry goes to Sentry Logs (not Issues) so we can see retry
          // rates and durations across sessions without cluttering triage.
          Sentry.logger.info("Anon auth retry", {
            attempt,
            attempt_duration_ms: attemptDuration,
            total_elapsed_ms: Date.now() - startedAt,
            online: navigator.onLine,
          });
          // Windowed throttle: enforce a minimum interval between attempts.
          // Fast failures (offline stack, blocked endpoint) get throttled;
          // slow failures (real network trying and giving up) retry immediately.
          if (attemptDuration < MIN_ATTEMPT_INTERVAL_MS) {
            await new Promise((r) =>
              setTimeout(r, MIN_ATTEMPT_INTERVAL_MS - attemptDuration),
            );
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, auth]);

  if (!user) {
    return null;
  }
  const value = { user };
  return <UserContext.Provider {...props} value={value} />;
};

export const useAuth = () => useContext(UserContext);
